import { Component, Prop, h, Element } from '@stencil/core';

/**
 * List of social links to display as icons (or icons with labels).
 * Renders each item as a link with a brand icon; when `showLabels` is `'true'`, a text label appears beside the icon.
 *
 * @example
 *   <me-socials
 *     items='[{"type": "linkedin", "href": "https://linkedin.com", "label": "LinkedIn"}, {"type": "x", "href": "https://x.com", "label": "X"}]'
 *   ></me-socials>
 */
@Component({
  tag: 'me-socials',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Socials {
  @Element() el!: HTMLElement;

  /**
   * Social link items to render.
   * @content
   * @type {array}
   * @items.type {object}
   * @items.property {string} type - Social network identifier used to resolve the brand icon (e.g. 'linkedin', 'x', 'dribbble')
   * @items.property {string} href - Full URL to the social media profile page
   * @items.property {string} label - Human-readable name of the social network, used as accessible link text and optional visible label
   * @items.required type
   * @items.required href
   * @items.required label
   * @items.content label
   * @minItems 1
   */
  @Prop() items!: any[];
  /** Show text labels next to icons ('true' or 'false') */
  @Prop() showLabels: string = 'false';

  componentWillLoad() {
    if (!Array.isArray(this.items)) {
      const itemsAttr = this.el.getAttribute('items');
      if (itemsAttr) {
        try {
          this.items = JSON.parse(itemsAttr.replace(/&quot;/g, '"'));
        } catch (e) {
          console.error('me-socials: Invalid JSON in items attribute:', e);
        }
      }
    }
  }

  render() {
    return (
      <div class="me-socials">
        {this.items &&
          this.items.map(item =>
            this.showLabels === 'true' ? (
              <div class="me-socials__item-wrapper">
                <me-button
                  class={`me-socials__item me-socials__item--${item.type}`}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                  variant="ghost"
                  icon={`brands--${item.type}`}
                  iconPosition="before"
                >
                  {item.label}
                </me-button>
              </div>
            ) : (
              <a class={`me-socials__item me-socials__item--${item.type}`} href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.label}>
                <me-icon name={`brands--${item.type}`} />
              </a>
            ),
          )}
      </div>
    );
  }
}
